/**
 * TremorPalmSkillEffect — Grit hurtmore-path apotheosis: delayed detonation.
 *
 * Touch an enemy at melee range. Walk away. Three seconds later, the
 * kinetic charge detonates from inside the target — massive AoE damage
 * centered on the target's current position. If the target dies before
 * detonation, 60% of the blast still fires from the death location.
 *
 * Phases: idle → armed (3000ms) → cooldown → idle.
 *
 * Extends SkillEffect directly (like DeadWeightSkillEffect) for
 * custom phase management with internal detonation timer.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Tremor Palm
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TremorPalmDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    remove(statusId: string, ...args: unknown[]): void;
  };
  readonly target: {
    hasTargetInRange(): boolean;
    getTargetEntityId(): string | undefined;
  };
  readonly deathTrigger: {
    register(config: {
      triggerId: string;
      sourceSkillId: string;
      requireWithinMs?: number;
      effect: { type: 'explosion'; damage: number; radiusTiles: number };
    }): void;
    unregister(triggerId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TremorPalmConfig {
  readonly cooldownMs: number;
  readonly detonationDelayMs: number;
  readonly earlyDeathDamageMultiplier: number;
}

const DEFAULT_CONFIG: TremorPalmConfig = {
  cooldownMs: 4000,                    // very_long cooldown tier
  detonationDelayMs: 3000,            // 3-second detonation delay
  earlyDeathDamageMultiplier: 0.6,    // 60% damage on early death
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type TremorPalmPhase = 'idle' | 'armed' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TremorPalmSkillEffect implements SkillEffect {
  readonly skillId = 'tremor_palm';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: TremorPalmDeps;
  private readonly config: TremorPalmConfig;

  private _phase: TremorPalmPhase = 'idle';
  private _phaseTimer = 0;

  // Detonation state
  private _chargeApplied = false;
  private _fullDetonationFired = false;
  private _earlyDetonationFired = false;

  constructor(deps: TremorPalmDeps, config: Partial<TremorPalmConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): TremorPalmPhase { return this._phase; }
  get chargeApplied(): boolean { return this._chargeApplied; }
  get fullDetonationFired(): boolean { return this._fullDetonationFired; }
  get earlyDetonationFired(): boolean { return this._earlyDetonationFired; }
  get detonationDelayMs(): number { return this.config.detonationDelayMs; }
  get earlyDeathDamageMultiplier(): number { return this.config.earlyDeathDamageMultiplier; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    if (this._phase !== 'idle') return false;
    return this.deps.target.hasTargetInRange();
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Reset detonation state
    this._chargeApplied = false;
    this._fullDetonationFired = false;
    this._earlyDetonationFired = false;

    // Apply tremor_charge status to the target
    this.deps.statusEffects.apply(
      'tremor_charge',
      this.config.detonationDelayMs,
      'tremor_palm',
    );
    this._chargeApplied = true;

    // Register early-death trigger via DeathTriggerSystem
    this.deps.deathTrigger.register({
      triggerId: 'tremor_palm_early',
      sourceSkillId: 'tremor_palm',
      requireWithinMs: this.config.detonationDelayMs,
      effect: {
        type: 'explosion',
        damage: this.config.earlyDeathDamageMultiplier,
        radiusTiles: 2.5,
      },
    });

    // Enter armed phase — detonation timer
    this._phase = 'armed';
    this._phaseTimer = this.config.detonationDelayMs;

    return true;
  }

  deactivate(): void {
    // Tremor Palm has no toggle — once activated it runs to completion
  }

  isActive(): boolean {
    return this._phase === 'armed';
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    this._phaseTimer -= delta;
    if (this._phaseTimer <= 0) {
      this.advancePhase();
    }
  }

  destroy(): void {
    if (this._chargeApplied && this._phase === 'armed') {
      this.deps.statusEffects.remove('tremor_charge', 'tremor_palm');
      this.deps.deathTrigger.unregister('tremor_palm_early');
    }
    this._phase = 'idle';
    this._phaseTimer = 0;
  }

  // --- External event handlers ---

  /**
   * Called by the game layer when the charged target dies before the
   * detonation timer expires. Fires a 60% damage detonation at the
   * death location and transitions to cooldown.
   */
  handleTargetDeath(): void {
    if (this._phase !== 'armed') return;
    if (this._fullDetonationFired) return; // already detonated normally

    // Unregister death trigger — we're handling it manually
    this.deps.deathTrigger.unregister('tremor_palm_early');

    // Fire 60% detonation
    this.deps.statusEffects.apply(
      'tremor_detonation',
      0,
      {
        damageMultiplier: this.config.earlyDeathDamageMultiplier,
        earlyDeath: true,
      },
      'tremor_palm',
    );
    this._earlyDetonationFired = true;

    // Clean up charge and enter cooldown
    this._chargeApplied = false;
    this._phase = 'cooldown';
    this._phaseTimer = this.config.cooldownMs;
  }

  // --- Internal ---

  private advancePhase(): void {
    switch (this._phase) {
      case 'armed':
        this.fireFullDetonation();
        break;
      case 'cooldown':
        this._phase = 'idle';
        this._phaseTimer = 0;
        break;
    }
  }

  private fireFullDetonation(): void {
    if (this._earlyDetonationFired) {
      // Target already died and 60% detonation fired — skip full detonation
      this._phase = 'cooldown';
      this._phaseTimer = this.config.cooldownMs;
      return;
    }

    // Full timer expired — unregister early death trigger
    this.deps.deathTrigger.unregister('tremor_palm_early');

    // Fire full detonation
    this.deps.statusEffects.apply(
      'tremor_detonation',
      0,
      {
        damageMultiplier: 1.0,
        earlyDeath: false,
      },
      'tremor_palm',
    );
    this._fullDetonationFired = true;
    this._chargeApplied = false;

    // Enter cooldown
    this._phase = 'cooldown';
    this._phaseTimer = this.config.cooldownMs;
  }
}
